import { useState } from 'react';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import type { Integration } from '@/types';

const MOCK_INTEGRATIONS: Integration[] = [
  {
    id: '1',
    name: 'Workday',
    provider: 'workday',
    status: 'connected',
    lastSync: '2026-03-25T08:30:00Z',
    logo: 'W',
  },
  {
    id: '2',
    name: 'BambooHR',
    provider: 'bamboohr',
    status: 'disconnected',
    logo: 'B',
  },
  {
    id: '3',
    name: 'ADP Workforce Now',
    provider: 'adp',
    status: 'connected',
    lastSync: '2026-03-24T22:15:00Z',
    logo: 'A',
  },
  {
    id: '4',
    name: 'Namely',
    provider: 'namely',
    status: 'disconnected',
    logo: 'N',
  },
];

const LOGO_COLORS: Record<string, string> = {
  W: 'bg-blue-600',
  B: 'bg-green-600',
  A: 'bg-red-600',
  N: 'bg-purple-600',
};

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>(MOCK_INTEGRATIONS);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const toggleConnection = (id: string) => {
    setIntegrations((prev) =>
      prev.map((integ) =>
        integ.id === id
          ? {
              ...integ,
              status: integ.status === 'connected' ? 'disconnected' : 'connected',
              lastSync: integ.status === 'disconnected' ? new Date().toISOString() : integ.lastSync,
            }
          : integ
      )
    );
  };

  const handleSync = (id: string) => {
    setSyncingId(id);
    setTimeout(() => {
      setIntegrations((prev) =>
        prev.map((integ) =>
          integ.id === id
            ? { ...integ, lastSync: new Date().toISOString() }
            : integ
        )
      );
      setSyncingId(null);
    }, 2000);
  };

  const formatSyncTime = (iso?: string) => {
    if (!iso) return 'Never';
    const date = new Date(iso);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
        <p className="mt-1 text-sm text-gray-500">
          Connect your HRIS to automatically sync workforce data
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {integrations.map((integ) => (
          <div key={integ.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`h-12 w-12 rounded-xl ${LOGO_COLORS[integ.logo]} flex items-center justify-center text-white font-bold text-lg`}
                >
                  {integ.logo}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{integ.name}</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {integ.status === 'connected' ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">
                          Connected
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          Disconnected
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {integ.status === 'connected' && (
              <p className="text-xs text-gray-400 mb-4">
                Last synced: {formatSyncTime(integ.lastSync)}
              </p>
            )}

            <div className="flex gap-2">
              <button
                className={
                  integ.status === 'connected' ? 'btn-secondary flex-1' : 'btn-primary flex-1'
                }
                onClick={() => toggleConnection(integ.id)}
              >
                {integ.status === 'connected' ? 'Disconnect' : 'Connect'}
              </button>
              {integ.status === 'connected' && (
                <button
                  className="btn-secondary"
                  onClick={() => handleSync(integ.id)}
                  disabled={syncingId === integ.id}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${
                      syncingId === integ.id ? 'animate-spin' : ''
                    }`}
                  />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card bg-gray-50">
        <h3 className="text-base font-semibold text-gray-900 mb-2">
          Need a different integration?
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          We support custom integrations via our REST API. You can also upload
          data manually via CSV on the Upload page.
        </p>
        <a href="/upload" className="text-sm font-medium text-primary-600 hover:text-primary-500">
          Go to Data Upload
        </a>
      </div>
    </div>
  );
}
